import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { getAuth, signOut } from 'firebase/auth';

import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

// Import the AddRestaurantFormPopup component
import AddRestaurantFormPopup from '../Components/PopUpAddResto'; // Adjust the path as needed

export default function RestaurantPage() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAddRestaurantOpen, setIsAddRestaurantOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  // Get current user on component mount
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setCurrentUser(user);
    } else {
      // Redirect to login if no user
      navigate('/login');
    }
  }, [auth, navigate]);

  // Fetch restaurants where ownerId = current user ID
  useEffect(() => {
    const fetchRestaurants = async () => {
      if (!currentUser?.uid) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const restaurantsRef = collection(db, 'restaurants');
        
        // Query restaurants where ownerId equals current user's UID
        const q = query(restaurantsRef, where('ownerId', '==', currentUser.uid));
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          setRestaurants([]);
          console.log('No restaurants found for this user');
        } else {
          const restaurantData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));
          
          setRestaurants(restaurantData);
          console.log(`Found ${restaurantData.length} restaurants`);
        }
      } catch (err) {
        console.error("Error fetching restaurants:", err);
        setError("Failed to load restaurants. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, [currentUser, db]);

  const handleSelectRestaurant = (restaurant) => {
    // Store selected restaurant in localStorage
    localStorage.setItem('restaurantId', restaurant.id);
    localStorage.setItem('selectedRestaurant', JSON.stringify(restaurant));
    
    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('restaurantSelected', { 
      detail: restaurant 
    }));
    
    // Navigate to home page (change this to your home page route)
    navigate('/home'); // or '/' if your home page is at root
  };

  const handleOpenAddRestaurant = () => {
    setIsAddRestaurantOpen(true);
  };

  const handleCloseAddRestaurant = () => {
    setIsAddRestaurantOpen(false);
    // Refresh restaurants list after adding a new one
    fetchRestaurantsAgain();
  };

  const fetchRestaurantsAgain = async () => {
    if (!currentUser?.uid) return;
    
    try {
      const restaurantsRef = collection(db, 'restaurants');
      const q = query(restaurantsRef, where('ownerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const restaurantData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRestaurants(restaurantData);
      } else {
        setRestaurants([]);
      }
    } catch (err) {
      console.error("Error refreshing restaurants:", err);
    }
  };

  // Handle delete restaurant
  const handleDeleteClick = (restaurant, event) => {
    event.stopPropagation(); // Prevent card click
    setRestaurantToDelete(restaurant);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!restaurantToDelete) return;
    
    setDeleting(true);
    
    try {
      // Delete restaurant from Firestore
      const restaurantRef = doc(db, 'restaurants', restaurantToDelete.id);
      await deleteDoc(restaurantRef);
      
      // Remove from local state
      setRestaurants(prevRestaurants => 
        prevRestaurants.filter(r => r.id !== restaurantToDelete.id)
      );
      
      setSnackbar({
        open: true,
        message: `Le restaurant "${restaurantToDelete.name || restaurantToDelete.restaurantName}" a été supprimé avec succès.`,
        severity: "success"
      });
      
      // Close dialog
      setDeleteDialogOpen(false);
      setRestaurantToDelete(null);
      
    } catch (err) {
      console.error("Error deleting restaurant:", err);
      setSnackbar({
        open: true,
        message: "Erreur lors de la suppression du restaurant. Veuillez réessayer.",
        severity: "error"
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setRestaurantToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      sessionStorage.clear();
      navigate('/');
    } catch (error) {
      console.error("Logout failed: ", error.message);
    }
  };

  const handleHelp = () => {
    // Open help/support
    window.open('/support', '_blank');
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#f5f7fb",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 2
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography color="text.secondary">
          Chargement de vos restaurants...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#f5f7fb",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 2,
          p: 3
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Réessayer
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          background: "#f5f7fb",
          padding: 4,
        }}
      >
        {/* HEADER */}
        <Box textAlign="center" mb={5}>
          <Typography fontSize={32} fontWeight={700}>
            Sélectionnez votre Restaurant
          </Typography>
          <Typography color="text.secondary">
            Choisissez le restaurant que vous souhaitez gérer
          </Typography>
          {currentUser && (
            <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
              Connecté en tant que: {currentUser.email}
            </Typography>
          )}
        </Box>

        {/* CARDS */}
        {restaurants.length === 0 ? (
          <Box textAlign="center" py={8}>
            <RestaurantIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Aucun restaurant trouvé
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Vous n'avez pas encore de restaurant. Créez votre premier restaurant!
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddRestaurant}
              sx={{
                background: "linear-gradient(135deg,#2563eb,#6d28d9)",
              }}
            >
              Ajouter un Restaurant
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3} justifyContent="center">
            {restaurants.map((restaurant) => (
              <Grid item key={restaurant.id}>
                <Card
                  sx={{
                    width: 280,
                    borderRadius: 3,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 28px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  {/* DELETE BUTTON */}
                  <IconButton
                    onClick={(e) => handleDeleteClick(restaurant, e)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      zIndex: 1,
                      '&:hover': {
                        backgroundColor: '#ef4444',
                        color: 'white'
                      },
                      transition: 'all 0.2s',
                      width: 32,
                      height: 32
                    }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  {/* TOP ICON */}
                  <Box
                    sx={{
                      height: 90,
                      background:
                        "linear-gradient(135deg,#2563eb,#6d28d9)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <RestaurantIcon sx={{ fontSize: 40, color: "white" }} />
                  </Box>

                  <CardContent sx={{ p: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography fontWeight={700} fontSize={18} noWrap>
                        {restaurant.name || restaurant.restaurantName}
                      </Typography>
                      <Chip 
                        label={restaurant.status || (restaurant.active ? "Active" : "En attente")} 
                        color={restaurant.active ? "success" : "warning"} 
                        size="small" 
                      />
                    </Box>

                    <Chip 
                      label={restaurant.type || "Restaurant"} 
                      size="small" 
                      sx={{ mb: 1.5, backgroundColor: '#f1f5f9' }} 
                    />

                    <Box display="flex" alignItems="center" gap={1} mb={0.7}>
                      <LocationOnIcon fontSize="small" sx={{ color: '#64748b' }} />
                      <Typography variant="body2" noWrap>
                        {restaurant.address || restaurant.city || "Adresse non spécifiée"}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mb={0.7}>
                      <PhoneIcon fontSize="small" sx={{ color: '#64748b' }} />
                      <Typography variant="body2">
                        {restaurant.phone || "Téléphone non spécifié"}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <PersonIcon fontSize="small" sx={{ color: '#64748b' }} />
                      <Typography variant="body2" noWrap>
                        {restaurant.matriculeFiscale || "Matricule fiscale non spécifié"}
                      </Typography>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => handleSelectRestaurant(restaurant)}
                      sx={{
                        borderRadius: 2,
                        fontSize: 13,
                        padding: "6px 0",
                        background:
                          "linear-gradient(135deg,#2563eb,#6d28d9)",
                      }}
                    >
                      Sélectionner
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* ADD RESTAURANT CARD */}
            <Grid item>
              <Box
                onClick={handleOpenAddRestaurant}
                sx={{
                  width: 280,
                  height: 310,
                  border: "2px dashed #cbd5e1",
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#64748b",
                  cursor: "pointer",
                  transition: 'all 0.2s',
                  "&:hover": { 
                    background: "#f1f5f9",
                    borderColor: "#2563eb",
                    color: "#2563eb"
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 40 }} />
                <Typography fontWeight={600} mt={1}>
                  Ajouter un Restaurant
                </Typography>
                <Typography variant="body2">
                  Créez un nouveau restaurant
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* LOGOUT */}
        <Box textAlign="center" mt={5}>
          <Typography 
            onClick={handleLogout}
            sx={{ 
              cursor: "pointer",
              '&:hover': { color: '#ef4444' }
            }} 
            color="text.secondary"
          >
            Se Déconnecter
          </Typography>
        </Box>

        {/* HELP BUTTON */}
        <IconButton
          onClick={handleHelp}
          sx={{
            position: "fixed",
            bottom: 25,
            right: 25,
            background: "#111",
            color: "white",
            "&:hover": { background: "#333" },
          }}
        >
          <HelpOutlineIcon />
        </IconButton>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Supprimer le restaurant
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Êtes-vous sûr de vouloir supprimer le restaurant 
            <strong style={{ color: '#2563eb', marginLeft: 4, marginRight: 4 }}>
              "{restaurantToDelete?.name || restaurantToDelete?.restaurantName}"
            </strong> ?
            <br /><br />
            Cette action est irréversible et toutes les données associées à ce restaurant seront définitivement supprimées.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            startIcon={<CloseIcon />}
            disabled={deleting}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            disabled={deleting}
            sx={{
              background: deleting ? '#ef4444' : '#dc2626',
              '&:hover': {
                background: '#b91c1c'
              }
            }}
          >
            {deleting ? "Suppression..." : "Supprimer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add Restaurant Form Popup */}
      <AddRestaurantFormPopup 
        open={isAddRestaurantOpen}
        onClose={handleCloseAddRestaurant}
        user={currentUser}
      />
    </>
  );
}